/* Hero product carousel: scroll-snap track + dots + hover arrows + autoplay.
   - Dots/arrows scroll to a card; swiping keeps the active dot in sync.
   - Autoplay (data-autoplay + data-interval) wraps around, pauses while the
     visitor hovers, focuses, or touches the carousel, and is disabled
     entirely under prefers-reduced-motion. Manual navigation resets the timer. */
(() => {
  if (customElements.get('ivb-hero-carousel')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class IvbHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ivb-hero__track');
      this.dots = Array.from(this.querySelectorAll('.ivb-hero__dot'));
      this.count = this.track ? this.track.children.length : 0;
      if (!this.track || this.count < 2) return;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(Number(dot.dataset.index));
          this.restartAutoplay();
        });
      });

      this.querySelectorAll('.ivb-hero__arrow').forEach((arrow) => {
        arrow.addEventListener('click', () => {
          this.goTo(this.currentIndex() + Number(arrow.dataset.direction));
          this.restartAutoplay();
        });
      });

      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.syncDots(), 80);
      }, { passive: true });

      // pause autoplay while the visitor is interacting
      this.addEventListener('pointerenter', () => this.stopAutoplay());
      this.addEventListener('pointerleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.track.addEventListener('touchend', () => this.startAutoplay(), { passive: true });

      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
    }

    currentIndex() {
      const cardWidth = this.track.children[0]?.offsetWidth || 1;
      return Math.round(this.track.scrollLeft / cardWidth);
    }

    goTo(index) {
      const wrapped = (index + this.count) % this.count;
      const card = this.track.children[wrapped];
      if (card) this.track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
    }

    syncDots() {
      const index = this.currentIndex();
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    startAutoplay() {
      if (!this.hasAttribute('data-autoplay') || REDUCED_MOTION.matches) return;
      this.stopAutoplay();
      const interval = Number(this.dataset.interval) || 5000;
      this.autoplayTimer = setInterval(() => this.goTo(this.currentIndex() + 1), interval);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('ivb-hero-carousel', IvbHeroCarousel);
})();
